/**
 * @fileoverview Action dispatcher for handling click events on action buttons.
 * @module utils/ActionDispatcher
 */

import { DEBUG_CBM } from '../constants.js';

/**
 * Maps CSS selectors to action handlers for click event delegation.
 * Provides a cleaner pattern for handling multiple action buttons.
 *
 * @example
 * const dispatcher = new ActionDispatcher(mapApp);
 * dispatcher.register('.delete-btn', 'actionOnItemDelete');
 * dispatcher.register('.save-btn', 'actionOnItemSave');
 *
 * // In click handler:
 * if (dispatcher.dispatch(event, item, lister)) {
 *     return; // Action was handled
 * }
 */
export class ActionDispatcher {
    /**
     * Creates a new ActionDispatcher.
     * @param {Object} actionTarget - Object containing action methods (usually mapApp)
     */
    constructor(actionTarget) {
        this.actionTarget = actionTarget;
        this.actions = new Map();
    }


    /**
     * Registers an action handler for a CSS selector.
     * @param {string} selector - CSS selector to match (e.g., '.delete-btn')
     * @param {string|Function} handler - Method name on actionTarget or direct function
     * @param {Object} [options] - Optional configuration
     * @param {boolean} [options.extractUid=true] - Whether to extract uid from element's dataset
     * @returns {ActionDispatcher} This instance for chaining
     */
    register(selector, handler, options = {}) {
        this.actions.set(selector, {
            handler,
            extractUid: options.extractUid !== false
        });
        return this;
    }


    /**
     * Attempts to dispatch a click event to a registered handler.
     * @param {Event} event - The click event
     * @param {Object} item - The clicked item data
     * @param {Object} [lister] - Optional lister instance
     * @returns {boolean} True if an action was handled, false otherwise
     */
    dispatch(event, item, lister = null) {
        if (DEBUG_CBM) {
            console.debug(`DEBUG: In ActionDispatcher dispatch, event = ${event}.`);
        }
        for (const [selector, config] of this.actions) {
            const target = event.target.closest(selector);
            if (target) {
                const uid = config.extractUid ? target.dataset.uid : null;
                this.executeHandler(config.handler, uid, item, lister);
                return true;
            }
        }
        return false;
    }


    /**
     * Executes a handler (either method name or function).
     * @param {string|Function} handler - Handler to execute
     * @param {string} uid - The item's unique identifier
     * @param {Object} item - The item data
     * @param {Object} lister - The lister instance
     * @private
     */
    executeHandler(handler, uid, item, lister) {
        if (typeof handler === 'function') {
            handler(uid, item, lister);
        } else if (typeof handler === 'string' && this.actionTarget) {
            const method = this.actionTarget[handler];
            if (typeof method === 'function') {
                method.call(this.actionTarget, uid, item, lister);
            } else {
                console.error(`ERROR: ActionDispatcher: Method "${handler}" not found on target`);
            }
        }
    }


    /**
     * Clears all registered actions.
     * @returns {ActionDispatcher} This instance for chaining
     */
    clear() {
        this.actions.clear();
        return this;
    }
}


/**
 * Default action selectors used across searchers.
 * @constant {Object}
 */
export const DEFAULT_ACTION_SELECTORS = {
    DELETE: '.item-delete-btn',
    EXECUTE_SEARCH: '.saved-search-execute-btn',
    // UNUSED?
    //SAVE: '.item-save-btn',
    //EDIT: '.item-edit-btn',
    //CLIPBOARD: '.item-clipboard-btn'
};
